import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Check, ExternalLink, Eye, EyeOff, Send } from "lucide-react";
import { toast } from "sonner";
import { getApiKey, setApiKey, fetchPrices } from "@/lib/twelve-data";
import { apiStatusStore, statusColor, statusLabel, useApiStatus } from "@/lib/api-status";
import { getMyRoles } from "@/lib/admin.functions";
import { telegramService } from "@/lib/telegram/telegramService";

export const Route = createFileRoute("/_authenticated/settings")({
  ssr: false,
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const { roles } = await getMyRoles();
    if (!roles.includes("admin")) throw redirect({ to: "/dashboard" });
  },
  component: SettingsPage,
});

function SettingsPage() {
  const { user } = Route.useRouteContext();
  const [push, setPush] = useState(true);
  const [emailNotif, setEmailNotif] = useState(true);
  const [highOnly, setHighOnly] = useState(false);

  const [apiKey, setApiKeyState] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const status = useApiStatus();

  // Telegram alerts
  const [tgToken, setTgToken] = useState("");
  const [tgChat, setTgChat] = useState("");
  const [tgMin, setTgMin] = useState(80);
  const [tgDaily, setTgDaily] = useState(false);
  const [tgTesting, setTgTesting] = useState(false);

  useEffect(() => {
    setTgToken(telegramService.getBotToken());
    setTgChat(telegramService.getChatId());
    setTgMin(telegramService.getMinConviction());
    setTgDaily(telegramService.getDailyBriefingEnabled());
  }, []);

  const saveTelegram = () => {
    telegramService.saveConfig({
      botToken: tgToken.trim(),
      chatId: tgChat.trim(),
      minConviction: tgMin,
      dailyBriefing: tgDaily,
    });
    toast.success("Alert settings saved");
  };
  const testTelegram = async () => {
    saveTelegram();
    setTgTesting(true);
    const ok = await telegramService.sendTest();
    setTgTesting(false);
    if (ok) toast.success("Test message sent to Telegram");
    else toast.error("Could not reach Telegram — check bot token and chat ID");
  };

  useEffect(() => {
    const k = getApiKey();
    if (k) setApiKeyState(k);
  }, []);

  const saveKey = async () => {
    setApiKey(apiKey);
    if (!apiKey.trim()) {
      apiStatusStore.set("no-key");
      toast.info("API key cleared — running in demo mode");
      return;
    }
    setTesting(true);
    try {
      await fetchPrices(["EURUSD"]);
      toast.success("Connected to Twelve Data");
    } catch (e) {
      toast.error("Could not connect", {
        description: e instanceof Error ? e.message : "Check your API key",
      });
    } finally {
      setTesting(false);
    }
  };

  const color = statusColor(status.status);

  return (
    <main className="flex-1 p-4 lg:p-6">
      <h1 className="mb-6 font-display text-2xl font-bold">Settings</h1>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-border/60 bg-card/60 p-6 lg:col-span-2">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="text-xs uppercase tracking-widest text-muted-foreground">
              API Configuration
            </h2>
            <div
              className="flex items-center gap-2 rounded-md border px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest"
              style={{
                borderColor: `color-mix(in oklab, ${color} 45%, transparent)`,
                backgroundColor: `color-mix(in oklab, ${color} 10%, transparent)`,
                color,
              }}
            >
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{
                  backgroundColor: color,
                  animation:
                    status.status === "connected" ? "pulse 1.6s ease-in-out infinite" : undefined,
                }}
              />
              {statusLabel(status.status)}
            </div>
          </div>
          <p className="mb-4 text-sm text-muted-foreground">
            Connect your Twelve Data API key to unlock live forex prices and real-time AI
            confluence signals.{" "}
            <a
              href="https://twelvedata.com/pricing"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              Get a free key <ExternalLink className="h-3 w-3" />
            </a>
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                Twelve Data API key
              </Label>
              <div className="relative mt-1.5">
                <Input
                  type={showKey ? "text" : "password"}
                  className="pr-10 font-mono"
                  placeholder="Enter your API key"
                  value={apiKey}
                  onChange={(e) => setApiKeyState(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowKey((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showKey ? "Hide key" : "Show key"}
                >
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              onClick={saveKey}
              disabled={testing}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {testing ? "Testing…" : "Save & Test"}
            </Button>
          </div>
          {status.message && (
            <div className="mt-3 text-xs text-muted-foreground">
              <span className="font-bold uppercase tracking-widest" style={{ color }}>
                {statusLabel(status.status)}:
              </span>{" "}
              {status.message}
            </div>
          )}
        </Card>

        <Card className="border-border/60 bg-card/60 p-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xs uppercase tracking-widest text-muted-foreground">
              Alerts — Telegram
            </h2>
            <Send className="h-4 w-4 text-muted-foreground" />
          </div>
          <p className="mb-4 text-sm text-muted-foreground">
            Receive 11-Agent Council signals directly in Telegram when conviction crosses your
            threshold. Create a bot with{" "}
            <a
              href="https://t.me/BotFather"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              @BotFather <ExternalLink className="h-3 w-3" />
            </a>{" "}
            and grab your chat ID from{" "}
            <a
              href="https://t.me/userinfobot"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              @userinfobot <ExternalLink className="h-3 w-3" />
            </a>
            .
          </p>
          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                Bot token
              </Label>
              <Input
                type="password"
                className="mt-1.5 font-mono"
                placeholder="123456:ABC-DEF..."
                value={tgToken}
                onChange={(e) => setTgToken(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                Chat ID
              </Label>
              <Input
                className="mt-1.5 font-mono"
                placeholder="123456789"
                value={tgChat}
                onChange={(e) => setTgChat(e.target.value)}
              />
            </div>
            <div className="lg:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                Min conviction — {tgMin}%
              </Label>
              <Slider
                className="mt-3"
                min={70}
                max={95}
                step={1}
                value={[tgMin]}
                onValueChange={(v) => setTgMin(v[0])}
              />
            </div>
            <div className="lg:col-span-2">
              <Toggle
                label="Daily briefing"
                desc="Sends a session summary every day at 08:00 UTC"
                checked={tgDaily}
                onChange={setTgDaily}
              />
            </div>
          </div>
          <div className="mt-5 flex gap-2">
            <Button
              onClick={saveTelegram}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Save settings
            </Button>
            <Button variant="outline" onClick={testTelegram} disabled={tgTesting}>
              {tgTesting ? "Sending…" : "Test connection"}
            </Button>
          </div>
        </Card>

        <Card className="border-border/60 bg-card/60 p-6">
          <h2 className="mb-5 text-xs uppercase tracking-widest text-muted-foreground">Profile</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input className="mt-1.5 font-mono" value={user.email ?? ""} readOnly />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Display name</Label>
              <Input className="mt-1.5" defaultValue={user.email?.split("@")[0]} />
            </div>
            <Button
              onClick={() => toast.success("Profile saved")}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Save changes
            </Button>
          </div>
        </Card>

        <Card className="border-border/60 bg-card/60 p-6">
          <h2 className="mb-5 text-xs uppercase tracking-widest text-muted-foreground">Notifications</h2>
          <div className="space-y-5">
            <Toggle
              label="Push notifications"
              desc="Real-time alerts on your device"
              checked={push}
              onChange={setPush}
            />
            <Toggle
              label="Email digests"
              desc="Daily summary at market open"
              checked={emailNotif}
              onChange={setEmailNotif}
            />
            <Toggle
              label="High-confluence only"
              desc="Notify when score ≥ 80"
              checked={highOnly}
              onChange={setHighOnly}
            />
          </div>
        </Card>

        <Card className="relative overflow-hidden border-primary/40 bg-card/80 p-6 shadow-[var(--shadow-glow)] lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Current plan</div>
              <div className="mt-1 font-display text-3xl font-bold">
                Pro <span className="text-base text-muted-foreground">— $29/mo</span>
              </div>
              <ul className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-muted-foreground">
                {[
                  "All 6 majors + Gold",
                  "Real-time signals",
                  "Full confluence breakdown",
                  "Risk calculator",
                  "Email + push alerts",
                  "7-day trial active",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-buy" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col gap-2">
              <Button variant="outline">Manage billing</Button>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                Upgrade to Institutional
              </Button>
            </div>
          </div>
        </Card>

        <DangerZone />
      </div>
    </main>
  );
}

function Toggle({
  label,
  desc,
  checked,
  onChange,
}: {
  label: string;
  desc: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{desc}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function DangerZone() {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState("");
  const onDelete = async () => {
    if (confirm !== "DELETE") {
      toast.error('Type "DELETE" to confirm');
      return;
    }
    if (!window.confirm("This will permanently delete your account, cancel any active subscription, and erase your data. Continue?")) return;
    setBusy(true);
    try {
      const { deleteMyAccount } = await import("@/lib/account.functions");
      await deleteMyAccount();
      const { supabase } = await import("@/integrations/supabase/client");
      await supabase.auth.signOut();
      toast.success("Account deleted");
      window.location.href = "/";
    } catch (err) {
      toast.error((err as Error).message ?? "Failed to delete account");
      setBusy(false);
    }
  };
  return (
    <Card className="border-destructive/40 p-6">
      <h2 className="text-lg font-semibold text-destructive">Danger zone</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Permanently delete your account and all associated data. This action is irreversible and will cancel any active subscription.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Input
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder='Type "DELETE" to confirm'
          className="max-w-xs"
        />
        <Button
          onClick={onDelete}
          disabled={busy || confirm !== "DELETE"}
          variant="destructive"
        >
          {busy ? "Deleting…" : "Delete my account"}
        </Button>
      </div>
    </Card>
  );
}
